import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadOrders from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrders";
import { getOrderStatusMappingDataView } from "@insite/client-framework/Store/Data/OrderStatusMappings/OrderStatusMappingsSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ordersListParameter from "@NA/Handlers/ADIOrdersListParameterHandler";
import selectedOrdersList from "@NA/Handlers/ADIOrdersSelectedListHandler";
import Pagination, { PaginationPresentationProps } from "@NA/MobiusOverrides/Pagination";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import ADIColors from "@NA/Utils/ADIColors";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import DataTable, { DataTablePresentationProps } from "@insite/mobius/DataTable";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderPresentationProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import ChevronUp from "@insite/mobius/Icons/ChevronUp";
import Icon from "@insite/mobius/Icon";
import X from "@insite/mobius/Icons/X";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { initialState } from "@insite/client-framework/Store/Data/Pages/PagesReducer";


const mapStateToProps = (state: CustomApplicationState) => ({
    settings: getSettingsCollection(state),
    ordersDataView: state.ADIGlobalData.ordersListStore.ordersList,
    getOrdersParameter: state.ADIGlobalData.ordersListStore.parameter,
    isLoading: state.ADIGlobalData.ordersListStore.isLoading,
    shouldLoadOrderStatusMappings: !getOrderStatusMappingDataView(state).value,
    location: getLocation(state),
    getPaginationData: state.ADIGlobalData.ordersListStore.ordersList?.pagination,
    isAuthenticated: state.context.session.isAuthenticated,
});

const mapDispatchToProps = {
    loadOrders,
    ordersListParameter,
    selectedOrdersList,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface TableStyles {
    wrapper?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    dataTable?: InjectableCss;
    dataTableHead?: InjectableCss;
    dataTableHeaders?: InjectableCss;
    dataTableBody?: InjectableCss;
    dataTableRow?: InjectableCss;
    Link?: LinkPresentationProps;
    DateHeader?: InjectableCss;
    NumberHeader?: InjectableCss;
    POHeader?: InjectableCss;
    ReferenceHeader?: InjectableCss;
    OrderStatus?: InjectableCss;
    TotalHeader?: InjectableCss;
    ItemsHeader?: InjectableCss;
    MoreDetailWrap?: InjectableCss;
    MoreDetailNoOfOrderLine?: InjectableCss;
    MoreDetailProductLineItems?: InjectableCss;
    MoreDetailProductLineItemImg?: InjectableCss;
    pagination?: PaginationPresentationProps;
    paginationWrap?: InjectableCss;
    viewDetailsButton?: ButtonProps;
    mobileGridContainer?: GridContainerProps;
    commanGridItem?: GridItemProps;
    tabCommanGridItem?: GridItemProps;
    dateAndViewGridItem?: GridItemProps;
    divider?: InjectableCss;
    creationDateText?: TypographyPresentationProps;
    viewDetailsLink?: LinkPresentationProps;
    mheading?: TypographyPresentationProps;
    mvalue?: TypographyPresentationProps;
    tabCreationDateText?: TypographyPresentationProps;
    tabTotal?: TypographyPresentationProps;
    tabHeaderGridContainer?: GridContainerProps;
    sortBtn?: ButtonProps;
    tableContainer?: InjectableCss;
    noteTextParent?: InjectableCss;
    noteText?: InjectableCss;
    defaultHeader?: InjectableCss;
    selectedOrderListTitle?: InjectableCss;
    nameCells?: InjectableCss;
    headerTitleWrapper?: InjectableCss;
    customDefaultHeader?: InjectableCss;
    footerBtnWrapper?: InjectableCss;
    continueBtn?: InjectableCss;
    cancelReturn?: InjectableCss;
    disableBtn?: InjectableCss;
    checkbox?: InjectableCss;
}

export const tableStyles: TableStyles = {
    paginationWrap: {
        css: css`
            margin-top: 30px;
            button {
                width: 43px;
                height: 43px;
                text-align: center;
                padding: 0 !important;
            }
            button[aria-current="page"] {
                background-color: ${ADIColors.white}!important;
            }
        `,
    },
    wrapper: {
        css: css`
            overflow: auto;
            width: 100%;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        size: 16,
        weight: 600,
    },
    dataTableHead: {
        css: css`
            margin-bottom: 12px;
            margin-top: 12px;
            padding-top: 4px;
            padding-bottom: 4px;
            background-color: ${ADIColors.white}!important;
            display: table-row;
            div {
                cursor: pointer;
                font-weight: 600;
                font-size: 16px;
            }
        `,
    },
    dataTableRow: {
        css: css`
            display: table-row;
            padding-top: 20px;
            padding-bottom: 20px;
        `,
    },

    DateHeader: {
        css: css`
            flex-basis: 10.33% !important;
            width: 10.33% !important;
            max-width: 136px;
            padding: 10px 0 10px 28px;
            ${TypographyStyle} {
                font-size: 14px;
                line-height: 20px;
                color: ${getColor("text.main")};
            }
            span div {
                display: inline-block;
                margin: 0px;
            }
            span svg {
                display: none;
            }
            &.sort-active {
                background-color: ${ADIColors.white2};
                span {
                    text-decoration: underline;
                }
                span svg {
                    display: inline-block;
                }
                span .DESC svg {
                    transform: rotate(180deg);
                }
            }
        `,
    },
    NumberHeader: {
        css: css`
            flex-basis: 7.85% !important;
            width: 7.85% !important;
            padding: 10px 0 10px 25px;
            max-width: 112px;
            span div {
                display: inline-block;
                margin: 0px 4px;
            }
            span svg {
                display: none;
            }
            &.sort-active {
                background-color: ${ADIColors.white2};
                span {
                    text-decoration: underline;
                }
                span svg {
                    display: inline-block;
                }
                span .DESC svg {
                    transform: rotate(180deg);
                }
            }
        `,
    },
    Link: {
        css: css`
            font-size: 14px;
            line-height: 20px;
            color: ${ADIColors.blue5};
            font-weight: bold;
            &:hover {
                text-decoration: underline;
            }
        `,
    },
    POHeader: {
        css: css`
            flex-basis: 9.1% !important;
            width: 9.1% !important;
            max-width: 226px;
            padding: 10px 0 10px 27px;
            span div {
                display: inline-block;
                margin: 0px 4px;
            }
            span svg {
                display: none;
            }
            &.sort-active {
                background-color: ${ADIColors.white2};
                span {
                    text-decoration: underline;
                }
                span svg {
                    display: inline-block;
                }
                span .DESC svg {
                    transform: rotate(180deg);
                }
            }
        `,
    },
    ReferenceHeader: {
        css: css`
            min-width: 140px;
            padding: 10px 0 10px 20px;
            span div {
                display: inline-block;
                margin: 0px 4px;
            }
            span svg {
                display: none;
            }
            &.sort-active {
                background-color: ${ADIColors.white2};
                span {
                    text-decoration: underline;
                }
                span svg {
                    display: inline-block;
                }
                span .DESC svg {
                    transform: rotate(180deg);
                }
            }
        `,
    },
    OrderStatus: {
        css: css`
            flex-basis: 10.8% !important;
            width: 10.8% !important;
            padding: 10px 0 10px 20px;
            span div {
                display: inline-block;
                margin: 0px 4px;
            }
            span svg {
                display: none;
            }
            &.sort-active {
                background-color: ${ADIColors.white2};
                span {
                    text-decoration: underline;
                }
                span svg {
                    display: inline-block;
                }
                span .DESC svg {
                    transform: rotate(180deg);
                }
            }
        `,
    },
    TotalHeader: {
        css: css`
            flex-basis: 8.89% !important;
            width: 8.89% !important;
            max-width: 115px;
            width: 115px;
            padding: 10px 16px 10px 20px;
            ${TypographyStyle} {
                max-width: 115px;
                width: 115px;
                margin-right: 4px;
                justify-content: flex-end;
                padding: 0 16px 0 0;
                font-size: 14px;
                color: ${getColor("text.main")};
                font-weight: bold;
                text-align: right;
            }
            span div {
                display: inline-block;
                margin: 0px 4px;
            }
            span svg {
                display: none;
            }
            &.sort-active {
                background-color: ${ADIColors.white2};
                span {
                    text-decoration: underline;
                }
                span svg {
                    display: inline-block;
                }
                span .DESC svg {
                    transform: rotate(180deg);
                }
            }
        `,
    },
    ItemsHeader: {
        css: css`
            flex-basis: 46.94% !important;
            width: 46.94% !important;
            border-left: solid 2px ${ADIColors.white2};
            padding: 10px 0px;
            padding-left: 19px;
        `,
    },
    MoreDetailWrap: {
        css: css`
            display: flex;
            flex-flow: row nowrap;
            flex-grow: 1;
            text-overflow: ellipsis;
            min-width: 0;
            white-space: normal;
            justify-content: flex-start;
            width: calc(100% - 31px);
            overflow: initial;
            margin: auto;
            align-items: center;
        `,
    },
    MoreDetailNoOfOrderLine: {
        css: css`
            min-width: 41px;
            width: 20px;
            display: inline-flex !important;
            ${TypographyStyle} {
                font-size: 14px;
            }
        `,
    },
    MoreDetailProductLineItems: {
        css: css`
            display: flex;
            width: calc(100% - 152px);
            padding-right: 22px;
            padding-left: 20px;
        `,
    },
    MoreDetailProductLineItemImg: {
        css: css`
            width: calc(20% - 15px);
            margin-right: 20px;
            float: left;
            height: 54px;
            max-height: 54px;
        `,
    },
    viewDetailsButton: {
        css: css`
            text-transform: none;
            border-radius: 4px;
            border: solid 1px ${ADIColors.blue1};
            font-size: 16px;
            font-weight: bold;
            line-height: normal;
            min-height: 40px;
            padding: 6px 10px !important;
            height: auto;
            max-width: 152px;
            ${TypographyStyle} {
                padding-left: 10px;
                padding-right: 10px;
            }
        `,
    },
    commanGridItem: {
        css: css`
            display: flex;
            justify-content: space-between;
            margin-top: 0;
            margin-bottom: 0;
            padding-top: 0;
            padding-bottom: 8px;
        `,
    },
    tabCommanGridItem: {
        css: css`
            display: flex;
            justify-content: space-between;
            margin-top: 0;
            margin-bottom: 0;
            padding-top: 10px;
            padding-bottom: 10px;
        `,
    },
    tabHeaderGridContainer: {
        css: css`
            background-color: ${ADIColors.white};
            padding: 10px;
            margin-top: 20px;
        `,
    },
    dateAndViewGridItem: {
        css: css`
            display: flex;
            justify-content: space-between;
            margin-top: 0;
            margin-bottom: 0;
            padding-top: 10px;
            padding-bottom: 10px;
        `,
    },
    mobileGridContainer: {
        css: css`
            margin-top: 10px;
            padding: 5px;
            padding-top: 20px;
            background-color: ${ADIColors.white};
        `,
    },
    divider: {
        css: css`
            width: 100%;
            border-bottom: 1px solid ${ADIColors.gray2};
            margin-bottom: 10px;
        `,
    },
    creationDateText: {
        css: css`
            text-transform: capitalize;
            font-size: 16px;
            line-height: 18px;
            margin-left: 0;
            color: ${ADIColors.darkBlue1};
            font-weight: bold;
        `,
    },
    viewDetailsLink: {
        css: css`
            padding-right: 0;
            font-size: 14px;
            position: relative;
            font-weight: bold;
            color: ${ADIColors.blue5};
            letter-spacing: 0.88px;
            text-decoration: none;
            padding-top: 2px;
            height: auto !important;
            position: relative;
            line-height: 14px;
            min-width: 120px;
        `,
    },
    mheading: {
        css: css`
            font-weight: bold;
            color: ${getColor("text.main")};
            font-size: 14px;
            line-height: 14px;
        `,
    },
    mvalue: {
        css: css`
            font-size: 14px;
            line-height: 14px;
        `,
    },
    tabCreationDateText: {
        css: css`
            font-size: 14px;
            line-height: 14px;
        `,
    },
    tabTotal: {
        css: css`
            margin-right: 4px;
            justify-content: flex-end;
            padding: 0 16px 0 0;
            font-size: 14px;
            color: ${getColor("text.main")};
            font-weight: bold;
            text-align: right;
        `,
    },
    sortBtn: {
        css: css`
            border: 1px solid ${getColor("common.border")};
            border-radius: 8px;
            background: ${getColor("common.background")};
            padding: 0 8px;
            margin: 0 10px;
            svg {
                transform: rotateX(180deg);
            }
            &:hover {
                background: ${getColor("common.background")};
            }
        `,
    },
    tableContainer: {
        css: css`
            overflow-x: auto;
            width: 100%;
        `,
    },
    noteTextParent: {
        css: css`
            margin-top:25px;
        `,
    },
    noteText: {
        css: css`
            background: ${ADIColors.danger} 0% 0% no-repeat padding-box;
            padding: 5px 5px 5px 10px;
            font-weight: bold;
            text-align: left;
            letter-spacing: -0.08px;
            color: ${ADIColors.white};
        `,
    },
    defaultHeader: {
        css: css`
            color: ${ADIColors.lightblue4};
            border: 1px solid ${ADIColors.gray2};
            cursor: pointer;
            font-weight: 600;
        `,
    },
    customDefaultHeader: {
        css: css`
            border: 1px solid ${ADIColors.gray2};
            font-weight: 600;
        `,
    },
    selectedOrderListTitle: {
        css: css`
            padding-left: 10px;
            font-weight: bold;
            text-align: left;
            letter-spacing: -0.08px;
            line-height: 2.25;
        `,
    },
    nameCells: {
        css: css`
            border: 1px solid ${ADIColors.gray2};
        `,
    },
    headerTitleWrapper: {
        css: css`
            display: flex;
            align-items: center;
            font-weight: inherit !important;
            span {
                color: ${ADIColors.lightblue4};
            }
        `,
    },
    footerBtnWrapper: {
        css: css`
            margin-top: 15px !important;
            margin-bottom: 20px !important;
            padding-top: 0px !important;
            display: flex;
            gap: 15px;
            justify-content: right;
        `,
    },
    continueBtn: {
        css: css`
            cursor: pointer;
            pointer-events: default;
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: ${ADIColors.lightRed2} !important;
            color: ${ADIColors.white};
            border-color: ${ADIColors.lightRed2} !important;
            :hover {
                background-color: ${ADIColors.lightRed2} !important;
                border-color: ${ADIColors.lightRed2} !important;
            }
        `,
    },
    disableBtn: {
        css: css`
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: ${ADIColors.gray3};
            color: ${ADIColors.white};
            border-color: ${ADIColors.gray3};
            pointer-events: none;
            :hover {
                background-color: ${ADIColors.gray3};
                border-color: ${ADIColors.gray3};
            }
        `,
    },
    cancelReturn: {
        css: css`
            border-radius: 4px;
            background-color: ${ADIColors.white};
            border-color: ${getColor("primary")};

            :hover {
                background-color: ${ADIColors.white};
                border-color: ${getColor("primary")};
            }
            span {
                color: ${getColor("primary")};
                font-size: inherit !important;
            }
        `,
    },
    checkbox: {
        css: css`
            text-align: center;
            justify-content: center;
            display: flex;
            span {
                border-color: ${ADIColors.black2} !important;
                box-shadow: none !important;
            }
        `,
    },
};

const styles = tableStyles;

const ADIReturnProduct: React.FunctionComponent<Props> = ({
    settings,
    ordersDataView,
    getOrdersParameter,
    shouldLoadOrderStatusMappings,
    location,
    getPaginationData,
    isLoading,
    loadOrders,
    ordersListParameter,
    selectedOrdersList,
    isAuthenticated,
}: Props) => {
    const history = useHistory();
    const [isMobile, setIsMobile] = React.useState<boolean>(false);
    const [sortOrder, setSortOrder] = React.useState("");
    const [selectedList, setSelectedList] = React.useState<any[]>([]);
    const [customerSequence, setcustomerSequence] = React.useState("-1");
    const [selectOrdersList, setSelectOrdersList] = React.useState<boolean>(false);
    const orders = ordersDataView?.orders;
    React.useEffect(() => {
        const parsedQuery = parseQueryString<{ orderNum: string }>(location.search);
        if (parsedQuery && parsedQuery?.orderNum && orders && orders?.length ) {
            let orderData = orders?.find(order => order?.webOrderNumber === parsedQuery?.orderNum);
            selectHandler(orderData);
        }
    }, [orders]);


    React.useEffect(() => {
        if (selectedList && selectOrdersList) {
            sessionStorage.setItem("selectedOrdersList", JSON.stringify(selectedList));
        }
    }, [selectOrdersList]);


    React.useEffect(() => {
        if (!isAuthenticated) {
            const returnUrl = location.pathname;
            window.location.href = `/MyAccount/SignIn?returnUrl=${encodeURIComponent(returnUrl)}`;
        }
    }, []);

    React.useEffect(() => {
        const onWindowResize = () => {
            if (window.innerWidth >= 768) {
                setIsMobile(false);
            } else {
                setIsMobile(true);
            }
        };
        onWindowResize();

        if (typeof window !== "undefined") {
            window.addEventListener("resize", onWindowResize);
        }

        return () => {
            if (typeof window !== "undefined") {
                window.removeEventListener("resize", onWindowResize);
            }
        };
    }, []);

    if (isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner}></LoadingSpinner>
            </StyledWrapper>
        );
    }

    const handleSortClick = (sortType: string) => {
        const isCurrentSortAsc = getOrdersParameter?.sort === sortType;
        const newSortOrder = isCurrentSortAsc ? `${sortType} DESC` : sortType;
        setSortOrder(newSortOrder);
        ordersListParameter({
            ...getOrdersParameter,
            customerSequence: customerSequence,
            sort: newSortOrder,
        });
    };


    const formatDate = (inputDate: any): string => {
        const date = new Date(inputDate);
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const day = date.getDate().toString().padStart(2, "0");
        const year = date.getFullYear().toString();

        const formattedDate = `${month}/${day}/${year}`;
        return formattedDate;
    };

    const rows = orders?.map(order => ({
        id: order.id,
        creationDate: formatDate(order.properties.creationDate),
        erpOrderNumber: order.erpOrderNumber,
        webOrderNumber: order.webOrderNumber,
        customerPO: order.customerPO,
        status: order.statusDisplay,
        orderTotalDisplay: order.orderTotalDisplay,
        noOfOrderLine: order.noOfOrderLine,
        productLineItems: order.productLineItems,
        orderDate: formatDate(order.orderDate),
    }));

    const changePage = (newPageIndex: number) => {
        ordersListParameter({
            ...getOrdersParameter,
            page: newPageIndex,
            currentPage: newPageIndex,
        });
    };

    const checkingOrderItem = (item: any) => {
        if (selectedList?.length > 0) {
            const index = selectedList?.findIndex(obj => obj?.webOrderNumber === item?.webOrderNumber);
            if (index !== -1) {
                return true;
            } else {
                return false;
            }
        }
    };
    const selectHandler = (item: any) => {
        const isOrderExist = checkingOrderItem(item);
        let newList = [];
        if (isOrderExist) {
            newList = selectedList?.filter(orderListItem => orderListItem?.webOrderNumber !== item?.webOrderNumber);
        } else {
            newList = [...selectedList, item];
        }
        setSelectedList(newList);
        selectedOrdersList({ selectedOrdersList: newList });


    };

    const continueHandler = () => {
        setSelectOrdersList((prev: boolean) => !prev)
        // sessionStorage.setItem("selectedOrdersList", JSON.stringify(selectedList));
        history.push("/Myaccount/Returns/SelectProducts");
        if (ordersDataView?.properties.p21ContactID != null &&
            ordersDataView?.properties.p21ContactID !== "" &&
            ordersDataView?.properties.p21ContactID !== '0') {
            localStorage.setItem("p21ContactID", ordersDataView?.properties.p21ContactID)
        }
    };
    const cancelReturnHandler = () => {
        setSelectedList([]);
        selectedOrdersList({ selectedOrdersList: [] })
        sessionStorage.removeItem("productsToBeReturned");
        history.push("/Myaccount/Returns/SelectOrders");



    };

    if (orders?.length === 0) {
        return (
            <StyledWrapper {...styles.noResultsContainer}>
                <Typography {...styles.noResultsText}>{translate("No orders found")}</Typography>
            </StyledWrapper>
        );
    }


    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.dataTable}>
                <StyledWrapper {...styles.noteTextParent}>
                    <StyledWrapper {...styles.noteText}>
                        {translate(
                            "To return products from multiple orders, start by selecting the orders using the check boxes below.",
                        )}
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.tableContainer} data-test-selector="userListUsersTable">
                    <DataTable {...styles.dataTable}>
                        <DataTableHead {...styles.dataTableHead}>
                            <DataTableHeader
                                {...styles.customDefaultHeader}
                                data-test-selector="userListUsersTable_defaultHeader"
                            >
                                {translate("Return")}
                            </DataTableHeader>
                            <DataTableHeader
                                {...styles.defaultHeader}
                                data-test-selector="userListUsersTable_unassignHeader"
                                onClick={() => handleSortClick("webOrderNumber")}
                            >
                                <StyledWrapper {...styles.headerTitleWrapper}>
                                    {translate("Number")}
                                    {getOrdersParameter?.sort.includes("webOrderNumber") ? (
                                        getOrdersParameter?.sort.includes("DESC") ? (
                                            <Icon src={ChevronUp} size={18} />
                                        ) : (
                                            <Icon src={ChevronDown} size={18} />
                                        )
                                    ) : (
                                        ""
                                    )}
                                </StyledWrapper>
                            </DataTableHeader>
                            <DataTableHeader
                                {...styles.defaultHeader}
                                data-test-selector="tst_userListUsersTable_billToHeader"
                                onClick={() => handleSortClick("customerPO")}
                            >
                                <StyledWrapper {...styles.headerTitleWrapper}>
                                    {translate("Purchase Order")}
                                    {getOrdersParameter?.sort.includes("customerPO") ? (
                                        getOrdersParameter?.sort.includes("DESC") ? (
                                            <Icon src={ChevronUp} size={18} />
                                        ) : (
                                            <Icon src={ChevronDown} size={18} />
                                        )
                                    ) : (
                                        ""
                                    )}
                                </StyledWrapper>
                            </DataTableHeader>
                            <DataTableHeader
                                {...styles.defaultHeader}
                                data-test-selector="userListUsersTable_addressHeader"
                                onClick={() => handleSortClick("ErpOrderNumber")}
                            >
                                <StyledWrapper {...styles.headerTitleWrapper}>
                                    {translate("Reference Number")}
                                    {getOrdersParameter?.sort.includes("ErpOrderNumber") ? (
                                        getOrdersParameter?.sort.includes("DESC") ? (
                                            <Icon src={ChevronUp} size={18} />
                                        ) : (
                                            <Icon src={ChevronDown} size={18} />
                                        )
                                    ) : (
                                        ""
                                    )}
                                </StyledWrapper>
                            </DataTableHeader>
                            <DataTableHeader
                                {...styles.defaultHeader}
                                data-test-selector="tst_userListUsersTable_cityHeader"
                                onClick={() => handleSortClick("status")}
                            >
                                <StyledWrapper {...styles.headerTitleWrapper}>
                                    {translate("Status")}
                                    {getOrdersParameter?.sort.includes("status") ? (
                                        getOrdersParameter?.sort.includes("DESC") ? (
                                            <Icon src={ChevronUp} size={18} />
                                        ) : (
                                            <Icon src={ChevronDown} size={18} />
                                        )
                                    ) : (
                                        ""
                                    )}
                                </StyledWrapper>
                            </DataTableHeader>
                            <DataTableHeader
                                {...styles.defaultHeader}
                                data-test-selector="tst_userListUsersTable_stateHeader"
                                onClick={() => handleSortClick("orderDate")}
                            >
                                <StyledWrapper {...styles.headerTitleWrapper}>
                                    {translate("Date")}
                                    {getOrdersParameter?.sort.includes("orderDate") ? (
                                        getOrdersParameter?.sort.includes("DESC") ? (
                                            <Icon src={ChevronUp} size={18} />
                                        ) : (
                                            <Icon src={ChevronDown} size={18} />
                                        )
                                    ) : (
                                        ""
                                    )}
                                </StyledWrapper>
                            </DataTableHeader>

                            <DataTableHeader
                                {...styles.defaultHeader}
                                data-test-selector="tst_userListUsersTable_stateHeader"
                                onClick={() => handleSortClick("noOfOrderLine")}
                            >
                                <StyledWrapper {...styles.headerTitleWrapper}>
                                    {translate("Lines")}
                                    {getOrdersParameter?.sort.includes("noOfOrderLine") ? (
                                        getOrdersParameter?.sort.includes("DESC") ? (
                                            <Icon src={ChevronUp} size={18} />
                                        ) : (
                                            <Icon src={ChevronDown} size={18} />
                                        )
                                    ) : (
                                        ""
                                    )}
                                </StyledWrapper>
                            </DataTableHeader>
                            <DataTableHeader
                                {...styles.defaultHeader}
                                data-test-selector="tst_userListUsersTable_stateHeader"
                                onClick={() => handleSortClick("OrderTotal")}
                            >
                                <StyledWrapper {...styles.headerTitleWrapper}>
                                    {translate("Total")}
                                    {getOrdersParameter?.sort.includes("OrderTotal") ? (
                                        getOrdersParameter?.sort.includes("DESC") ? (
                                            <Icon src={ChevronUp} size={18} />
                                        ) : (
                                            <Icon src={ChevronDown} size={18} />
                                        )
                                    ) : (
                                        ""
                                    )}
                                </StyledWrapper>
                            </DataTableHeader>
                        </DataTableHead>
                        <DataTableBody {...styles.dataTableBody}>
                            {rows?.map((items: any) => {
                                const {
                                    erpOrderNumber,
                                    webOrderNumber,
                                    customerPO,
                                    status,
                                    orderTotalDisplay,
                                    noOfOrderLine,
                                    orderDate
                                } = items;
                                return (
                                    <DataTableRow
                                        {...styles.dataTableRow}
                                        key={webOrderNumber}
                                        data-test-selector="userListUsersTable_row"
                                        data-test-key={webOrderNumber}
                                    >
                                        <DataTableCell
                                            {...styles.nameCells}
                                            data-test-selector="userListUsersTable_cell_name"
                                        >
                                            <Checkbox
                                                {...styles.checkbox}
                                                labelPosition="right"
                                                onChange={() => selectHandler(items)}
                                                checked={checkingOrderItem(items)}
                                            ></Checkbox>
                                        </DataTableCell>

                                        <DataTableCell
                                            {...styles.nameCells}
                                            data-test-selector="userListUsersTable_cell_name"
                                        >
                                            {webOrderNumber}
                                        </DataTableCell>
                                        <DataTableCell
                                            {...styles.nameCells}
                                            data-test-selector="userListUsersTable_cell_name"
                                        >
                                            {customerPO}
                                        </DataTableCell>
                                        <DataTableCell
                                            {...styles.nameCells}
                                            data-test-selector="userListUsersTable_cell_name"
                                        >
                                            {erpOrderNumber}
                                        </DataTableCell>

                                        <DataTableCell
                                            {...styles.nameCells}
                                            data-test-selector="userListUsersTable_cell_username"
                                        >
                                            {status}
                                        </DataTableCell>
                                        <DataTableCell
                                            {...styles.nameCells}
                                            data-test-selector="userListUsersTable_cell_status"
                                        >
                                            {orderDate}
                                        </DataTableCell>
                                        <DataTableCell
                                            {...styles.nameCells}
                                            data-test-selector="userListUsersTable_cell_role"
                                        >
                                            {noOfOrderLine}
                                        </DataTableCell>
                                        <DataTableCell
                                            {...styles.nameCells}
                                            data-test-selector="userListUsersTable_cell_lastSignIn"
                                        >
                                            {orderTotalDisplay}
                                        </DataTableCell>
                                    </DataTableRow>
                                );
                            })}
                        </DataTableBody>
                    </DataTable>

                    {getPaginationData && (
                        <StyledWrapper {...styles.paginationWrap}>
                            <Pagination
                                {...styles.pagination}
                                currentPage={getPaginationData.currentPage}
                                resultsPerPage={getPaginationData.pageSize}
                                resultsCount={getPaginationData.totalItemCount}
                                resultsPerPageOptions={[]}
                                onChangePage={changePage}
                                onChangeResultsPerPage={() => { }}
                            />
                        </StyledWrapper>
                    )}

                    {/* SELECTED LIST */}
                    {selectedList && Array.isArray(selectedList) && selectedList.length > 0 && (
                        <>
                            <StyledWrapper {...styles.selectedOrderListTitle}>
                                {translate("Orders Selected to Return Products")}
                            </StyledWrapper>

                            <DataTable {...styles.dataTable}>
                                <DataTableHead {...styles.dataTableHead}>
                                    <DataTableHeader
                                        {...styles.customDefaultHeader}
                                        data-test-selector="userListUsersTable_defaultHeader"
                                    >
                                        {translate("Remove")}
                                    </DataTableHeader>
                                    <DataTableHeader
                                        {...styles.customDefaultHeader}
                                        data-test-selector="userListUsersTable_unassignHeader"
                                    >
                                        {translate("Number")}
                                    </DataTableHeader>
                                    <DataTableHeader
                                        {...styles.customDefaultHeader}
                                        data-test-selector="tst_userListUsersTable_billToHeader"
                                    >
                                        {translate("Purchase Order")}
                                    </DataTableHeader>
                                    <DataTableHeader
                                        {...styles.customDefaultHeader}
                                        data-test-selector="userListUsersTable_addressHeader"
                                    >
                                        {translate("Reference Number")}
                                    </DataTableHeader>
                                    <DataTableHeader
                                        {...styles.customDefaultHeader}
                                        data-test-selector="tst_userListUsersTable_cityHeader"
                                    // onSortClick={() => headerClick("city")}
                                    >
                                        {translate("Status")}
                                    </DataTableHeader>
                                    <DataTableHeader
                                        {...styles.customDefaultHeader}
                                        data-test-selector="tst_userListUsersTable_stateHeader"
                                    >
                                        {translate("Date")}
                                    </DataTableHeader>

                                    <DataTableHeader
                                        {...styles.customDefaultHeader}
                                        data-test-selector="tst_userListUsersTable_stateHeader"
                                    >
                                        {translate("Lines")}
                                    </DataTableHeader>
                                    <DataTableHeader
                                        {...styles.customDefaultHeader}
                                        data-test-selector="tst_userListUsersTable_stateHeader"
                                    >
                                        {translate("Total")}
                                    </DataTableHeader>
                                </DataTableHead>
                                <DataTableBody {...styles.dataTableBody}>
                                    {selectedList && selectedList?.length>0 && selectedList .filter((item) => item).map(items => {
                                        const {
                                            erpOrderNumber,
                                            webOrderNumber,
                                            customerPO,
                                            status,
                                            orderTotalDisplay,
                                            noOfOrderLine,
                                            orderDate,
                                        } = items;
                                        return (
                                            <DataTableRow
                                                {...styles.dataTableRow}
                                                key={webOrderNumber}
                                                data-test-selector="userListUsersTable_row"
                                                data-test-key={webOrderNumber}
                                            >
                                                <DataTableCell
                                                    {...styles.nameCells}
                                                    data-test-selector="userListUsersTable_cell_name"
                                                    data-test-key={webOrderNumber}
                                                    onClick={() => {
                                                        selectHandler(items);
                                                    }}
                                                >
                                                    <Icon src={X} size={18} />
                                                </DataTableCell>

                                                <DataTableCell
                                                    {...styles.nameCells}
                                                    data-test-selector="userListUsersTable_cell_name"
                                                    data-test-key={webOrderNumber}
                                                >
                                                    {webOrderNumber}
                                                </DataTableCell>
                                                <DataTableCell
                                                    {...styles.nameCells}
                                                    data-test-selector="userListUsersTable_cell_name"
                                                    data-test-key={webOrderNumber}
                                                >
                                                    {customerPO}
                                                </DataTableCell>
                                                <DataTableCell
                                                    {...styles.nameCells}
                                                    data-test-selector="userListUsersTable_cell_name"
                                                    data-test-key={webOrderNumber}
                                                >
                                                    {erpOrderNumber}
                                                </DataTableCell>

                                                <DataTableCell
                                                    {...styles.nameCells}
                                                    data-test-selector="userListUsersTable_cell_username"
                                                    data-test-key={webOrderNumber}
                                                >
                                                    {status}
                                                </DataTableCell>
                                                <DataTableCell
                                                    {...styles.nameCells}
                                                    data-test-selector="userListUsersTable_cell_status"
                                                    data-test-key={webOrderNumber}
                                                >
                                                    {orderDate}
                                                </DataTableCell>
                                                <DataTableCell
                                                    {...styles.nameCells}
                                                    data-test-selector="userListUsersTable_cell_role"
                                                    data-test-key={webOrderNumber}
                                                >
                                                    {noOfOrderLine}
                                                </DataTableCell>
                                                <DataTableCell
                                                    {...styles.nameCells}
                                                    data-test-selector="userListUsersTable_cell_lastSignIn"
                                                    data-test-key={webOrderNumber}
                                                >
                                                    {orderTotalDisplay}
                                                </DataTableCell>
                                            </DataTableRow>
                                        );
                                    })}
                                </DataTableBody>
                            </DataTable>
                        </>
                    )}

                    <GridItem width={12} {...styles.footerBtnWrapper}>
                        <Button
                            {...styles.cancelReturn}
                            onClick={() => {
                                cancelReturnHandler();
                            }}
                            data-test-selector="orders_clearFiltersButton"
                        >
                            {translate("Cancel Return")}
                        </Button>
                        <Button
                            {... (selectedList.length > 0 &&
                                (ordersDataView?.properties.p21ContactID != null &&
                                    ordersDataView?.properties.p21ContactID !== "" &&
                                    ordersDataView?.properties.p21ContactID !== '0'))
                                ? styles.continueBtn
                                : styles.disableBtn}
                            onClick={() => {
                                continueHandler();
                            }}
                            data-test-selector="orders_clearFiltersButton"
                            disabled={
                                !(selectedList.length > 0 ||
                                    (ordersDataView?.properties.p21ContactID != null &&
                                        ordersDataView?.properties.p21ContactID !== "" &&
                                        ordersDataView?.properties.p21ContactID !== '0'))
                            }
                        >
                            {translate("Continue")} <Icon color={ADIColors.white} src={ChevronRight} size={15} />
                        </Button>
                    </GridItem>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ADIReturnProduct),
    definition: {
        group: "My Account",
        displayName: "ADI Return Product",
    },
};

export default widgetModule;
